import { createEntityId } from '@/core/EntityId';
import {
  TAG_CATEGORIES,
  TAG_EMPHASES,
  compareTags,
  normalizeLabel,
  normalizeNote,
  tagPhrase,
  type TagCategory,
  type TagEmphasis,
} from '@/semantics/SemanticTag';

/**
 * Semantic tags placed on a reference photograph, before any site exists.
 *
 * ## Why the import needs its own tag
 *
 * `semantics/SemanticTag` describes a *document*: its targets are a plant instance, a site object, a
 * painted material, a patch of world ground. None of those exist while a photograph is being read —
 * the whole point of the pipeline is that it is about to invent them. So the target here is the only
 * thing that does exist at that moment: a place in the image.
 *
 * Everything else is deliberately the same object. The six categories, the three emphases, the
 * length limits, the sort order and the prompt phrasing are imported from the tag layer rather than
 * restated, because a designer who has learned that "essential" outranks "mention" on a bench should
 * not have to learn it again on a photograph. The only new vocabulary is the target.
 *
 * ## One kind of target: a place in the frame
 *
 * A tag points at somewhere you can put a finger on, and nothing else. There used to be a second
 * scope — "the whole photograph" — for statements about the place as a whole, and it was a mistake
 * twice over: it made the panel ask "which of these two things do you mean" before every tag, and it
 * collected exactly the material that already has a home. Direction that is about the site rather
 * than about a spot belongs in the direction box, which every stage reads verbatim and which does
 * not require clicking anything first.
 *
 * ## Tagging is worth doing, and nothing waits for it
 *
 * The pipeline's first stage is where every later stage's error is born: it fixes the scale, the site
 * extent and the region list that stages 2–5 all quote as authoritative. It is also the stage with
 * the least evidence — a photograph, and nothing about what the photograph is *of*. A model told
 * nothing will read a gravel bin store as a fire-pit lounge, a paddock as a lawn, and a neighbour's
 * roof as this site's garage, and it will do so confidently and differently on every run.
 *
 * Thirty seconds of pointing at things is still the cheapest correction available anywhere in the
 * pipeline. It is no longer *required* to start one: a gate that blocks a run is only worth its cost
 * when nothing else can supply the input, and {@link ReferenceTagSuggest} can now make a first pass
 * at it. What the designer does with those suggestions — accept, edit, delete, add — is where the
 * value is, and that is worth inviting rather than compelling.
 *
 * ## Where a tag came from travels with it
 *
 * {@link ReferenceTag.origin} separates what a person said from what a pre-pass guessed, and
 * {@link referenceTagBrief} keeps the two apart in the prompt. A model's guess quoted back to five
 * later stages as "the designer, who knows this site, says" would be laundering an inference into
 * evidence — the exact failure this whole feature is built to avoid. Editing a suggestion makes it
 * the designer's, because at that point someone has actually looked at it.
 *
 * ## Image space, and only image space
 *
 * Targets are normalized `[u, v]` in `[0, 1]` with the origin at the image's top-left — the same
 * space stage 1 speaks and the same space `ui/PipelineOverlay` draws in. Reach is a fraction of the
 * image *width*, so a marker is a true circle whatever the photograph's aspect ratio. Nothing here
 * knows about metres: the conversion to world coordinates only becomes possible once stage 1 has
 * fixed the site extent, and {@link referenceTagBrief} does it then, from that artifact.
 *
 * Pure data and pure functions over it — no DOM, no Three.js. `ui/ReferenceTagger` is the half that
 * needs a browser.
 */

/**
 * Where in the frame a tag points.
 *
 * A circle: a centre in normalized image coordinates and a reach that is a fraction of the image
 * *width*, so the shape stays a circle on a panoramic photograph.
 */
export interface ReferenceTagTarget {
  u: number;
  v: number;
  radius: number;
}

/**
 * Who placed the tag.
 *
 * `designer` is a statement from someone who has seen the real site; `suggested` is a pre-pass
 * model's reading of the pixels, which is a useful starting point and not evidence. The prompt keeps
 * them in separate sections for that reason.
 */
export type ReferenceTagOrigin = 'designer' | 'suggested';

export interface ReferenceTag {
  id: string;
  target: ReferenceTagTarget;
  category: TagCategory;
  /** The noun phrase itself — short, and what the prompt quotes. */
  label: string;
  /** Optional elaboration, appended to the prompt line when present. */
  note?: string;
  emphasis: TagEmphasis;
  origin: ReferenceTagOrigin;
  /** ISO 8601, so the list can be shown newest-first without a separate ordering field. */
  createdAt: string;
}

/** Reach of a freshly placed tag, as a fraction of the image width. */
export const DEFAULT_REFERENCE_TAG_RADIUS = 0.06;
/** Smallest and largest reach a tag may claim. */
export const MIN_REFERENCE_TAG_RADIUS = 0.015;
export const MAX_REFERENCE_TAG_RADIUS = 0.5;

export interface CreateReferenceTagOptions {
  target: ReferenceTagTarget;
  label: string;
  category?: TagCategory;
  note?: string;
  emphasis?: TagEmphasis;
  /** Defaults to `designer` — the only way a tag arrives without someone typing it is a pre-pass. */
  origin?: ReferenceTagOrigin;
  /** Overrides `new Date()` so tests and snapshots stay stable. */
  now?: Date;
}

export function createReferenceTag(options: CreateReferenceTagOptions): ReferenceTag {
  const tag: ReferenceTag = {
    id: createEntityId('reftag-'),
    target: normalizeReferenceTarget(options.target),
    category: TAG_CATEGORIES.includes(options.category as TagCategory)
      ? (options.category as TagCategory)
      : 'identity',
    label: normalizeLabel(options.label),
    emphasis: TAG_EMPHASES.includes(options.emphasis as TagEmphasis)
      ? (options.emphasis as TagEmphasis)
      : 'mention',
    origin: options.origin === 'suggested' ? 'suggested' : 'designer',
    createdAt: (options.now ?? new Date()).toISOString(),
  };
  const note = normalizeNote(options.note);
  if (note !== undefined) tag.note = note;
  return tag;
}

/** Clamp a target into the frame. A tag outside the image describes nothing the model can see. */
export function normalizeReferenceTarget(target: ReferenceTagTarget): ReferenceTagTarget {
  return {
    u: clamp(finite(target.u, 0.5), 0, 1),
    v: clamp(finite(target.v, 0.5), 0, 1),
    radius: clamp(
      finite(target.radius, DEFAULT_REFERENCE_TAG_RADIUS),
      MIN_REFERENCE_TAG_RADIUS,
      MAX_REFERENCE_TAG_RADIUS,
    ),
  };
}

/** Deep copy, so a held list can never alias a live one. */
export function cloneReferenceTag(tag: ReferenceTag): ReferenceTag {
  const copy: ReferenceTag = { ...tag, target: { ...tag.target } };
  if (tag.note === undefined) delete copy.note;
  return copy;
}

/** Loudest first, then newest — the same order the tag panel uses. */
export function compareReferenceTags(a: ReferenceTag, b: ReferenceTag): number {
  return compareTags(a, b);
}

/** Two targets are the same when they point at the same place; reach is not identity. */
export function sameReferenceTarget(a: ReferenceTagTarget, b: ReferenceTagTarget): boolean {
  return a.u === b.u && a.v === b.v;
}

/* ---------------------------------- Summary ---------------------------------- */

export interface ReferenceTagSummary {
  total: number;
  /** How many came from the pre-pass and have not been touched since. */
  suggested: number;
  /** Whether anything has been *named* — the class of statement segmentation cannot recover. */
  named: boolean;
}

export function summarizeReferenceTags(tags: readonly ReferenceTag[]): ReferenceTagSummary {
  const meaningful = tags.filter((tag) => tag.label.length > 0);
  return {
    total: meaningful.length,
    suggested: meaningful.filter((tag) => tag.origin === 'suggested').length,
    named: meaningful.some((tag) => tag.category === 'identity'),
  };
}

/**
 * One line describing the state of the tagging, for a stage row or a status strip.
 *
 * Phrased as encouragement rather than as a requirement, because it is no longer one — but it still
 * says what the tags are worth, since "0 tags" on its own reads as a field nobody filled in rather
 * than as the cheapest correction in the pipeline going unspent.
 */
export function describeReferenceTags(tags: readonly ReferenceTag[]): string {
  const summary = summarizeReferenceTags(tags);
  if (summary.total === 0) return 'Nothing tagged — optional, and the cheapest way to steer stage 1.';
  const places = `${summary.total} place${summary.total === 1 ? '' : 's'} tagged`;
  const suggested = summary.suggested > 0 ? `  ·  ${summary.suggested} suggested, unreviewed` : '';
  return `${places}${suggested}`;
}

/* ----------------------------------- Prompt ----------------------------------- */

/** Site extent from stage 1, when the brief can be quoted in world metres as well as image space. */
export interface ReferenceProjection {
  widthMeters: number;
  lengthMeters: number;
}

/** A tag's place on the finished site: a centre in world metres and a reach around it. */
export interface ProjectedReferenceTarget {
  x: number;
  z: number;
  radiusMeters: number;
}

/**
 * A tag's place in the frame, as world metres on the extent stage 1 fixed.
 *
 * The naive projection, and deliberately the same one `ui/PipelineOverlay` draws with — no
 * perspective correction, because there is nothing here to correct it against. It has three callers
 * that must not disagree: the prompt brief quotes it to stages 2–5, the overlay draws it, and
 * `PipelineSemantics` uses it to plant the designer's tags on the compiled site. Three
 * implementations of one formula is three chances for a bin store to move.
 */
export function projectReferenceTarget(
  target: ReferenceTagTarget,
  projection: ReferenceProjection,
): ProjectedReferenceTarget {
  return {
    x: (target.u - 0.5) * projection.widthMeters,
    z: (target.v - 0.5) * projection.lengthMeters,
    // Reach is a fraction of the image WIDTH, so it converts against the width alone.
    radiusMeters: target.radius * projection.widthMeters,
  };
}

/**
 * The tags as a prompt section.
 *
 * Four decisions worth stating, all inherited from `docs/SEMANTIC_TAGS.md` because they were learned
 * the expensive way there:
 *
 * - **Coordinates, not just words.** A tag is quoted with the point it was placed on, so the model
 *   can bind "bin store" to a specific part of the frame instead of hunting for one. Once stage 1 has
 *   fixed the site extent the same point is also given in world metres, using the same naive
 *   projection `ui/PipelineOverlay` draws with — so what the later stages read is exactly what the
 *   inspector shows.
 * - **A person's statements and a pre-pass's guesses are separated**, and only the first are
 *   described as knowledge of the real site. A model reading its own earlier guess back as testimony
 *   would compound the error rather than correct it, and it would do so invisibly.
 * - **Negatives are pulled out.** `avoid` tags are gathered into one refusal block at the end rather
 *   than left inline: a model reading "no shed here" inside a list of things that are there will
 *   build the shed.
 * - **Emphasis is spelled out in words**, by {@link tagPhrase}, rather than left to list position.
 */
export function referenceTagBrief(
  tags: readonly ReferenceTag[],
  projection?: ReferenceProjection,
): string {
  const meaningful = [...tags].filter((tag) => tag.label.length > 0).sort(compareReferenceTags);
  if (meaningful.length === 0) return '';

  const positives = meaningful.filter((tag) => tag.category !== 'avoid');
  const refusals = meaningful.filter((tag) => tag.category === 'avoid');

  const sections: string[] = [];

  const byDesigner = positives.filter((tag) => tag.origin !== 'suggested');
  if (byDesigner.length > 0) {
    sections.push(
      'The designer has looked at this photograph and marked it up. These statements come from ' +
        'someone who knows the real site; they are observations you could not make from pixels, and ' +
        'they override anything you would otherwise infer.\n\n' +
        `## Marked in the frame\n\n${byDesigner.map((tag) => `- ${spotLine(tag, projection)}`).join('\n')}`,
    );
  }

  const suggested = positives.filter((tag) => tag.origin === 'suggested');
  if (suggested.length > 0) {
    sections.push(
      '## Read off the photograph by an earlier automatic pass — NOT confirmed by anyone\n\n' +
        'These labels came from a model looking at the same pixels you are, and the designer has not ' +
        'reviewed them. Treat them as a starting point of equal standing to your own reading, not as ' +
        'evidence: where you disagree with one, trust your own eyes and say so in "notes".\n\n' +
        suggested.map((tag) => `- ${spotLine(tag, projection)}`).join('\n'),
    );
  }

  if (refusals.length > 0) {
    sections.push(
      `## Must NOT appear\n\n${refusals.map((tag) => `- ${refusalLine(tag, projection)}`).join('\n')}`,
    );
  }

  return `# What has been tagged on the photograph\n\n${sections.join('\n\n')}`;
}

function spotLine(tag: ReferenceTag, projection?: ReferenceProjection): string {
  return `${where(tag.target, projection)} ${tagPhrase(tag)}`;
}

function refusalLine(tag: ReferenceTag, projection?: ReferenceProjection): string {
  const note = tag.note !== undefined ? ` — ${tag.note}` : '';
  const unconfirmed = tag.origin === 'suggested' ? ' (suggested by the automatic pass, unconfirmed)' : '';
  return `${where(tag.target, projection)} ${tag.label}${note}${unconfirmed}`;
}

/**
 * Where a tag is, in whichever spaces are known.
 *
 * Image space always; world metres too once stage 1 has fixed the extent, because a stage working in
 * metres should not have to redo a conversion the pipeline has already committed to.
 */
function where(target: ReferenceTagTarget, projection?: ReferenceProjection): string {
  const image = `image [${round(target.u, 3)}, ${round(target.v, 3)}], reach ${round(target.radius, 3)} of the image width`;
  if (!projection) return `At ${image}:`;
  const { x, z, radiusMeters } = projectReferenceTarget(target, projection);
  return `At ${image} — world approx (x ${round(x, 1)} m, z ${round(z, 1)} m), reach ~${round(radiusMeters, 1)} m:`;
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finite(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}
